import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Clock,
	Target,
	UserPlus,
	FileCheck,
	Activity,
} from "lucide-react";
import { KPICard } from "./KPICard";
import { FunnelChart } from "./FunnelChart";
import { ConversionTable } from "./ConversionTable";
import { ScoreComparisonChart } from "./ScoreComparisonChart";
import { VelocityChart } from "./VelocityChart";
import { RecruiterTable } from "./RecruiterTable";
import { RecruiterActivityTable } from "./RecruiterActivityTable";
import { CandidateCreationChart } from "./CandidateCreationChart";
import { KPITooltip } from "./KPITooltip";
import type { DashboardKPIs } from "@/hooks/dashboard/useDashboardKPIs";

interface AnalyticsPrintViewProps {
	data: DashboardKPIs;
	dateFrom: string;
	dateTo: string;
}

export const AnalyticsPrintView = forwardRef<HTMLDivElement, AnalyticsPrintViewProps>(
	({ data, dateFrom, dateTo }, ref) => {
		const { t } = useTranslation();

		return (
			<div
				ref={ref}
				style={{
					position: "absolute",
					left: "-9999px",
					top: 0,
					width: "1200px",
					backgroundColor: "#ffffff",
					color: "#1a1a1a",
					fontFamily: "system-ui, -apple-system, sans-serif",
					padding: "40px",
				}}
			>
				{/* Report Header */}
				<div style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					borderBottom: "2px solid #0E2356",
					paddingBottom: "20px",
					marginBottom: "30px",
				}}>
					<img
						src="/img/FullLogo.png"
						alt="Digger"
						style={{ height: "40px" }}
						crossOrigin="anonymous"
					/>
					<div style={{ textAlign: "right" }}>
						<div style={{ fontSize: "22px", fontWeight: 700, color: "#0E2356" }}>
							{t("dashboard.kpi.pdf.reportTitle")}
						</div>
						<div style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
							{dateFrom} — {dateTo}
						</div>
						<div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
							{t("dashboard.kpi.pdf.generatedOn", {
								date: new Date().toLocaleDateString(),
							})}
						</div>
					</div>
				</div>

				{/* KPI Content — same layout as Analytics.tsx */}
				<div className="space-y-6">
					{/* Row 1: KPI Cards */}
					<div className="grid grid-cols-4 gap-4">
						<KPICard
							title={t("dashboard.kpi.timeToHire")}
							value={data.timeToHire != null ? `${data.timeToHire}d` : "—"}
							subtitle={data.timeToFill != null
								? `${t("dashboard.kpi.timeToFill", "Time to fill")}: ${data.timeToFill}d`
								: t("dashboard.kpi.timeToHireDesc")}
							icon={Clock}
							color="bg-blue-50 text-blue-500"
						/>
						<KPICard
							title={t("dashboard.kpi.offerAcceptance")}
							value={data.offerAcceptanceRate.offers > 0
								? `${Math.round((data.offerAcceptanceRate.accepted / data.offerAcceptanceRate.offers) * 100)}%`
								: "—"}
							subtitle={`${data.offerAcceptanceRate.accepted}/${data.offerAcceptanceRate.offers} ${t("dashboard.kpi.offers")}`}
							icon={Target}
							color="bg-emerald-50 text-emerald-500"
						/>
						<KPICard
							title={t("dashboard.kpi.candidatesCreated")}
							value={data.candidatesCreated > 0 ? String(data.candidatesCreated) : "—"}
							subtitle={data.candidatesByStatus.slice(0, 2).map(s => `${s.status}: ${s.count}`).join(", ") || undefined}
							icon={UserPlus}
							color="bg-purple-50 text-purple-500"
						/>
						<KPICard
							title={t("dashboard.kpi.publicAppRatio", "Public Application Ratio")}
							value={data.publicAppRatio.publicApps + data.publicAppRatio.matches > 0
								? `${data.publicAppRatio.ratio}%`
								: "—"}
							subtitle={data.publicAppRatio.publicApps + data.publicAppRatio.matches > 0
								? `${data.publicAppRatio.publicApps} public / ${data.publicAppRatio.matches} matches`
								: undefined}
							icon={FileCheck}
							color="bg-amber-50 text-amber-500"
						/>
					</div>

					{/* Row 2: Pipeline Funnel + Velocity */}
					<div className="grid grid-cols-2 gap-4">
						<FunnelChart data={data.pipelineFunnel} />
						<VelocityChart data={data.historicalVelocity} />
					</div>

					{/* Row 2b: Candidate Creation Trend */}
					<CandidateCreationChart
						total={data.candidatesCreated}
						trend={data.candidateCreationTrend}
						byStatus={data.candidatesByStatus}
					/>

					{/* Row 3: Conversion Rates + AI Score */}
					<div className="grid grid-cols-2 gap-4">
						<ConversionTable
							conversions={data.conversionRates}
							dropOffs={data.dropOffRates}
						/>
						<ScoreComparisonChart
							scoreVsOutcome={data.aiScoreVsOutcome}
							distribution={data.scoreDistribution}
						/>
					</div>

					{/* Row 4: Matches per Job + Recruiter Productivity */}
					<div className="grid grid-cols-2 gap-4">
						<Card>
							<CardHeader>
								<div className="flex items-center gap-2">
									<CardTitle className="text-lg">{t("dashboard.kpi.matchesPerJob")}</CardTitle>
								</div>
							</CardHeader>
							<CardContent>
								<div className="grid grid-cols-3 gap-4 mb-6">
									<div className="text-center p-3 rounded-sm bg-muted/50">
										<div className="text-2xl font-semibold">{data.matchesPerJob.avg}</div>
										<div className="text-xs text-muted-foreground">{t("dashboard.kpi.avg")}</div>
									</div>
									<div className="text-center p-3 rounded-sm bg-muted/50">
										<div className="text-2xl font-semibold">{data.matchesPerJob.min}</div>
										<div className="text-xs text-muted-foreground">{t("dashboard.kpi.min")}</div>
									</div>
									<div className="text-center p-3 rounded-sm bg-muted/50">
										<div className="text-2xl font-semibold">{data.matchesPerJob.max}</div>
										<div className="text-xs text-muted-foreground">{t("dashboard.kpi.max")}</div>
									</div>
								</div>

								{data.timeInStage.length > 0 && (
									<div>
										<div className="flex items-center gap-2 mb-3">
											<h4 className="text-sm font-medium text-muted-foreground">
												{t("dashboard.kpi.timeInStage")}
											</h4>
										</div>
										<div className="space-y-2">
											{data.timeInStage.map((s) => (
												<div key={s.stage} className="flex items-center justify-between text-sm">
													<span>{t(`dashboard.kpi.stages.${s.stage}`, s.stage)}</span>
													<span className="font-medium">{s.avgDays}d</span>
												</div>
											))}
										</div>
									</div>
								)}
							</CardContent>
						</Card>

						<RecruiterTable data={data.recruiters} />
					</div>

					{/* Row 4b: Recruiter Activity */}
					<RecruiterActivityTable data={data.recruiterActivity} />

					{/* Row 5: Pipeline Health */}
					<Card>
						<CardHeader>
							<div className="flex items-center gap-2">
								<Activity className="h-5 w-5 text-muted-foreground" />
								<CardTitle className="text-lg">{t("dashboard.kpi.pipelineHealth")}</CardTitle>
							</div>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-4 gap-4">
								<div className="text-center p-4 rounded-sm bg-blue-50">
									<div className="text-2xl font-semibold text-blue-500">{data.pipelineHealth.openJobs}</div>
									<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthOpenJobs")}</div>
								</div>
								<div className="text-center p-4 rounded-sm bg-green-50">
									<div className="text-2xl font-semibold text-green-600">{data.pipelineHealth.activeCandidates}</div>
									<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthActiveCandidates")}</div>
								</div>
								<div className="text-center p-4 rounded-sm bg-purple-50">
									<div className="text-2xl font-semibold text-purple-600">{data.pipelineHealth.matchesInProgress}</div>
									<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthMatchesInProgress")}</div>
								</div>
								<div className="text-center p-4 rounded-sm bg-amber-50">
									<div className="text-2xl font-semibold text-amber-600">{data.pipelineHealth.pendingMatches}</div>
									<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthPendingMatches")}</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Report Footer */}
				<div style={{
					borderTop: "1px solid #e5e7eb",
					paddingTop: "16px",
					marginTop: "40px",
					textAlign: "center",
					fontSize: "10px",
					color: "#999",
				}}>
					{t("dashboard.kpi.pdf.footer")}
				</div>
			</div>
		);
	}
);

AnalyticsPrintView.displayName = "AnalyticsPrintView";
